import { POLL_TIME, PROCESSING_PERCENTAGE_FULL } from 'constants/constants';
import { BulkDownloadFormatEnum } from 'gql/graphql';
import { toaster } from 'helpers/toaster';
import { useTranslations } from 'next-intl';
import { useTranslationRequestBulkDownload } from 'providers/TranslationRequests/useTranslationRequestBulkDownload';
import { useTranslationRequestBulkDownloadStatus } from 'providers/TranslationRequests/useTranslationRequestBulkDownloadStatus';
import { useCallback, useEffect, useState } from 'react';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import { downloadExternalFile } from 'utils/downloadExternalFile';

interface IUseDownloadBulkFileIn {
  versions: string[];
}

interface IUseDownloadBulkFileOut {
  disabled: boolean;
  loading: boolean;
  onDownload: (format: BulkDownloadFormatEnum) => () => Promise<void>;
}

const useDownloadBulkFile = ({ versions }: IUseDownloadBulkFileIn): IUseDownloadBulkFileOut => {
  const t = useTranslations();

  const [guid, setGuid] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);

  const { data: dataStatus, isError } = useTranslationRequestBulkDownloadStatus(
    {
      guid
    },
    {
      enabled: !!guid,
      staleTime: 0,
      gcTime: 0,
      refetchInterval: !!guid ? POLL_TIME : false
    }
  );

  const toastError = useCallback(
    (error: unknown) => {
      notifyException(error);
      toaster.error(t('translation.tr.files.error_files'));
    },
    [t]
  );

  const { mutateAsync: translationRequestBulkDownload, isPending: isLoadingMutating } =
    useTranslationRequestBulkDownload({
      onError: toastError
    });

  const disabled = isLoadingMutating;

  const isLoadingStatus = isLoadingMutating || (loading && !dataStatus?.file) || downloading;

  const onDownload = (format: BulkDownloadFormatEnum) => async () => {
    if (!isLoadingStatus) {
      setLoading(true);
      const { guid: guidIncoming } = await translationRequestBulkDownload({
        translationRequestVersion: versions,
        format
      });
      setGuid(guidIncoming);
    }
  };

  const downloadFiles = useCallback(async () => {
    if (dataStatus?.file && !downloading) {
      try {
        setDownloading(true);
        await downloadExternalFile(dataStatus?.file ?? '');
        setGuid('');
        toaster.success(t('translation.tr.files.success_files'));
        setLoading(false);
        return setDownloading(false);
      } catch (error) {
        toastError(error);
        setLoading(false);
        return setDownloading(false);
      }
    }

    if (
      !dataStatus?.file &&
      dataStatus?.progress === PROCESSING_PERCENTAGE_FULL &&
      dataStatus.status !== 'processing'
    ) {
      toastError(t('translation.tr.files.error_no_file'));
      setLoading(false);
      setDownloading(false);
      return setGuid('');
    }
  }, [dataStatus?.file, dataStatus?.progress, dataStatus?.status, downloading, t, toastError]);

  useEffect(() => {
    downloadFiles();
  }, [downloadFiles]);

  useEffect(() => {
    if (isError) {
      setLoading(false);
      setDownloading(false);
      setGuid('');
      notifyException(dataStatus, ESpansException.BulkDownloadStatusError);
      toaster.error(t('translation.tr.files.error_files'));
    }
  }, [dataStatus, isError, t, toaster]);

  return { disabled, loading: isLoadingStatus, onDownload };
};

export default useDownloadBulkFile;
